import React, {
  createContext, ReactNode, useContext, useMemo, useState,
} from 'react';

// item 76 — the BRAND SOURCE: which branding the app renders, chosen per-device on
// Tools → Appearance and persisted in localStorage. A co-branded middle ground between
// full white-label (item 35) and stock Basix:
//  - own         → the company's full brand package (colours + font + logo)
//  - basixColors → stock Basix colours + font, the company's OWN logo
//  - stock       → the full stock Basix kit (no company logo)
//
// This lives in its OWN tiny, Chakra-free provider mounted ABOVE BrandThemeProvider
// (which builds the Chakra theme): the theme depends on the brand source, so the
// source cannot live in AppearanceProvider — that sits *inside* the ChakraProvider.

export type BrandSource = 'own' | 'basixColors' | 'stock';

const KEY = 'basix.appearance.brandSource';
const ALLOWED: BrandSource[] = ['own', 'basixColors', 'stock'];

interface BrandSourceContextValue {
  brandSource: BrandSource;
  setBrandSource: (s: BrandSource) => void;
}

const BrandSourceContext = createContext<BrandSourceContextValue>({
  brandSource: 'own',
  setBrandSource: () => {},
});

const read = (): BrandSource => {
  try {
    const v = window.localStorage.getItem(KEY) as BrandSource | null;
    return v && ALLOWED.includes(v) ? v : 'own';
  } catch {
    return 'own';
  }
};

export const BrandSourceProvider = ({ children }: { children: ReactNode }) => {
  const [brandSource, setState] = useState<BrandSource>(read);

  const setBrandSource = (s: BrandSource) => {
    setState(s);
    try { window.localStorage.setItem(KEY, s); } catch { /* ignore */ }
  };

  const value = useMemo<BrandSourceContextValue>(
    () => ({ brandSource, setBrandSource }),
    [brandSource],
  );

  return <BrandSourceContext.Provider value={value}>{children}</BrandSourceContext.Provider>;
};

export const useBrandSource = (): BrandSourceContextValue => useContext(BrandSourceContext);

export default BrandSourceProvider;
